import React, { ReactNode } from 'react';

interface BadgeProps {
  children?: ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'gray' | 'subtle';
  color?: string; // For compatibility, this is an alias for variant
  size?: 'sm' | 'md' | 'lg';
  pill?: boolean;
  className?: string;
  icon?: ReactNode;
  label?: string; // For compatibility, alternative to children
  onClick?: (e: React.MouseEvent) => void; // Clickable badges
}

const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'primary',
  color,
  size = 'md',
  pill = false,
  className = '',
  icon,
  label,
  onClick,
}) => {
  // Map color names to their respective classes
  // This mapping is needed for subtle variant and for when color is provided as a string
  const colorToClass: Record<string, string> = {
    'primary': 'text-red bg-red',
    'secondary': 'text-blue bg-blue',
    'red': 'text-red bg-red',
    'blue': 'text-blue bg-blue',
    'green': 'text-success bg-success',
    'success': 'text-success bg-success',
    'error': 'text-error bg-error',
    'danger': 'text-error bg-error',
    'warning': 'text-warning bg-warning',
    'info': 'text-info bg-info',
    'gray': 'text-gray-700 dark:text-gray-300 bg-gray-500',
    'purple': 'text-purple-700 dark:text-purple-300 bg-purple-500',
    'indigo': 'text-indigo-700 dark:text-indigo-300 bg-indigo-500',
    'orange': 'text-orange-700 dark:text-orange-300 bg-orange-500',
    'gold': 'text-yellow-700 dark:text-yellow-300 bg-yellow-500',
    'silver': 'text-gray-700 dark:text-gray-300 bg-gray-400',
    'bronze': 'text-orange-700 dark:text-orange-300 bg-orange-600',
  };
  
  // Get the effective color class
  const resolvedColor = (color && colorToClass[color.toLowerCase()]) || 
                        (variant !== 'subtle' && colorToClass[variant]) || 
                        colorToClass.primary;
  
  // If color is provided, use it as an alias for variant
  const effectiveVariant = variant;
  
  // Base styles
  const baseStyle = `inline-flex items-center ${onClick ? 'cursor-pointer' : ''}`;
  
  // Variant styles
  const variantStyles: Record<string, string> = {
    primary: 'bg-red text-white dark:bg-red-600 dark:text-white',
    secondary: 'bg-blue text-white dark:bg-blue-600 dark:text-white',
    success: 'bg-green-600 text-white dark:bg-green-700 dark:text-white',
    danger: 'bg-red-600 text-white dark:bg-red-700 dark:text-white',
    warning: 'bg-yellow-500 text-white dark:bg-yellow-600 dark:text-white',
    info: 'bg-blue-500 text-white dark:bg-blue-600 dark:text-white',
    gray: 'bg-gray-500 text-white dark:bg-gray-600 dark:text-white',
    gold: 'bg-yellow-500 text-white dark:bg-yellow-600 dark:text-white',
    silver: 'bg-gray-400 text-white dark:bg-gray-500 dark:text-white',
    bronze: 'bg-orange-600 text-white dark:bg-orange-700 dark:text-white',
    // Subtle variant uses the color prop to determine text/border color with very light background
    subtle: `${resolvedColor.split(' ')[0]} bg-opacity-5 dark:bg-opacity-10 border border-current border-opacity-20`
  };
  
  // Size styles
  const sizeStyles = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-0.5',
    lg: 'text-base px-3 py-1',
  };
  
  // Border radius
  const roundedStyle = pill ? 'rounded-full' : 'rounded-md';
  
  // Combine styles
  const combinedClassName = `${baseStyle} ${variantStyles[effectiveVariant] || variantStyles.primary} ${sizeStyles[size]} ${roundedStyle} ${className}`;
  
  return (
    <span 
      className={combinedClassName}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {icon && <span className="mr-1">{icon}</span>}
      {label || children}
    </span>
  );
};

export default Badge;